import { z } from 'zod';
import {
  makeStandardAccessorialSchema,
  makeStandardDriverRequirementSchema,
  makeStandardFreightModeSchema,
} from '../../enums';
import {
  makeStandardCarrierSchema,
  makeStandardDimensionsSchema,
  makeStandardEquipmentOptionsSchema,
  makeStandardFreightOrderStopSchema,
  makeStandardReferenceSchema,
  makeStandardWeightSchema,
} from '../freightOrder';
import type { StandardFreightOrderStop } from '../freightOrder';
import { MaskedMovementDataSchema } from '../maskedMovement';
import { RatingMethod } from '../pricingStrategy';
import { makeStandardCustomerChargesSchema } from './charges';
import { makeStandardMovementCostsSchema } from './costs';
import { makeStandardPricingDataSchema } from './pricingData';

export const makeStandardCarrierRateSchema = () => {
  return z.object({
    id: z.string().optional(),
    carrierId: z.string().optional(),
    rate: z.number().optional(),
    ratingMethod: z.nativeEnum(RatingMethod).optional(),
    externalUpdatedAt: z.string().optional(),
  });
};

const standardCarrierRateSchema = makeStandardCarrierRateSchema();
export type StandardCarrierRate = z.infer<typeof standardCarrierRateSchema>;

// NOTE(Martín): this is an initial list of statuses we support.
// we may need to add more statuses in the future.
// we may need to rename some statuses in the future.
// we may need to remove some statuses in the future.
export enum MovementStatus {
  CANCELLED = 'CANCELLED',
  DELIVERED = 'DELIVERED',
  DISPATCH = 'DISPATCH',
  READY_FOR_DISPATCH = 'READY_FOR_DISPATCH',
  IN_TRANSIT = 'IN_TRANSIT',
  AT_PICKUP = 'AT_PICKUP',
  AT_DELIVERY = 'AT_DELIVERY',
  UNKNOWN = 'UNKNOWN',
}

export const makeStandardMovementStatusSchema = () => {
  return z.nativeEnum(MovementStatus);
};

export enum CoverageStatus {
  AVAILABLE = 'AVAILABLE',
  CREATED = 'CREATED',
  COVERED = 'COVERED',
  DELIVERED = 'DELIVERED',
  DISPATCHED = 'DISPATCHED',
  LOADED = 'LOADED',
  RELEASED_TO_BILLING = 'RELEASED_TO_BILLING',
  TENDER_ACCEPTED = 'TENDER_ACCEPTED',
  TENDER_OFFERED = 'TENDER_OFFERED',
  TENDERED = 'TENDERED',
  UNLOADED = 'UNLOADED',
  UNKNOWN = 'UNKNOWN',
  VOIDED = 'VOIDED',
}

export const COVERED_STATUSES: CoverageStatus[] = [
  CoverageStatus.COVERED,
  CoverageStatus.DISPATCHED,
  CoverageStatus.LOADED,
  CoverageStatus.DELIVERED,
];

export const makeStandardCoverageStatusSchema = () => {
  return z.nativeEnum(CoverageStatus);
};

export const isCoverageStatus = (value: unknown): value is CoverageStatus =>
  typeof value === 'string' &&
  (Object.values(CoverageStatus) as string[]).includes(value);

// NOTE(cara): App versions that predate coverageStatus still update only
// tmsStatus, so it is the freshest value until those versions are retired.
export const getCoverageStatus = (
  movement?: { coverageStatus?: CoverageStatus; tmsStatus?: string } | null
): CoverageStatus | undefined => {
  const tmsStatus = movement?.tmsStatus;
  if (isCoverageStatus(tmsStatus)) return tmsStatus;
  return movement?.coverageStatus;
};

export const hasCoveredStatus = (
  movement?: { coverageStatus?: CoverageStatus; tmsStatus?: string } | null
): boolean => {
  const coverageStatus = getCoverageStatus(movement);
  return (
    coverageStatus !== undefined && COVERED_STATUSES.includes(coverageStatus)
  );
};

// NOTE(cara): Mirrors the value into tmsStatus so app versions that only read
// tmsStatus stay correct during rollout and after a rollback.
export const toCoverageStatusFields = (
  coverageStatus: CoverageStatus | undefined
): { coverageStatus?: CoverageStatus; tmsStatus?: CoverageStatus } =>
  coverageStatus === undefined
    ? {}
    : { coverageStatus, tmsStatus: coverageStatus };

export enum TrackingInfoSource {
  MACROPOINT = 'MACROPOINT',
  TRUCKER_TOOLS = 'TRUCKER_TOOLS',
  SAMSARA = 'SAMSARA',
  MOTIVE = 'MOTIVE',
  UNKNOWN = 'UNKNOWN',
}

export const makeStandardTrackingInfoSchema = () =>
  z.object({
    url: z.string().trim().min(1),
    source: z.nativeEnum(TrackingInfoSource),
  });

const standardTrackingInfoSchema = makeStandardTrackingInfoSchema();
export type StandardTrackingInfo = z.infer<typeof standardTrackingInfoSchema>;

export const getTrackingSourceFromUrl = (url: string): TrackingInfoSource => {
  const lower = url.trim().toLowerCase();
  if (lower.includes('macropoint')) return TrackingInfoSource.MACROPOINT;
  if (lower.includes('truckertools')) return TrackingInfoSource.TRUCKER_TOOLS;
  if (lower.includes('samsara')) return TrackingInfoSource.SAMSARA;
  if (lower.includes('gomotive')) return TrackingInfoSource.MOTIVE;
  return TrackingInfoSource.UNKNOWN;
};

export const makeStandardContractedMovementSchema = () => {
  return z.object({
    id: z.string().optional(),
    externalId: z.string().optional(),
    externalUpdatedAt: z.string().optional(),
    mode: makeStandardFreightModeSchema().optional(),
    weight: makeStandardWeightSchema().optional(),
    dimensions: makeStandardDimensionsSchema().optional(),
    stops: z.array(makeStandardFreightOrderStopSchema()),
    // NOTE(andrew): 'costs' is deprecated. Use additionalCosts + carrierRate.
    costs: makeStandardMovementCostsSchema().optional(),
    additionalCosts: makeStandardMovementCostsSchema().optional(),
    customerCharges:
      makeStandardCustomerChargesSchema().optional() /* TODO(farzaan): remove this */,
    equipment: makeStandardEquipmentOptionsSchema().optional(),
    driverRequirement: makeStandardDriverRequirementSchema().optional(),
    accessorials: z.array(makeStandardAccessorialSchema()).optional(),
    attributes: z.record(z.string(), z.string()).optional(),
    carrier: makeStandardCarrierSchema().optional(),
    carrierRate: makeStandardCarrierRateSchema().optional(),
    pricingData: makeStandardPricingDataSchema().optional(),
    status: makeStandardMovementStatusSchema().optional(),
    coverageStatus: makeStandardCoverageStatusSchema().optional(),
    tmsStatus: z.string().optional(),
    onHold: z.boolean().optional(),
    externalNotes: z.string().optional(),
    references: z.array(makeStandardReferenceSchema()).optional(),
    maskedMovementData: MaskedMovementDataSchema.optional(),
    trackingInfo: makeStandardTrackingInfoSchema().optional(),
  });
};
const standardContractedMovementSchema = makeStandardContractedMovementSchema();
export type VoomaContractedMovement = z.infer<
  typeof standardContractedMovementSchema
> & {
  // NOTE(mike): Remove this once the Stop schema is cleaned up.
  stops: StandardFreightOrderStop[];
};
